/**
 * Abstürze vom letzten Lauf ins Protokoll. (23.09.2026)
 *
 * Martin: *„Also stürzt Vespera jetzt die ganze Zeit ab."* Am Server kam dazu
 * keine einzige Zeile an. Zwei Arten sehen für ihn gleich aus und haben
 * verschiedene Ursachen:
 *
 * 1. **Die ganze App stirbt** (nativ). Ab Build 71 hält PawbotAbsturz.swift
 *    den Bericht fest; hier wird er beim nächsten Start abgeholt.
 * 2. **Nur der Seiteninhalt stirbt.** iOS beendet den WebKit-Prozess (meist
 *    Speicher), die Hülle lädt die Seite neu — für Martin ein Absturz, für
 *    die App ein frischer Start. Erkennbar an einer Marke im
 *    sessionStorage: sie überlebt das Neuladen derselben Ansicht, wird aber
 *    bei jedem ordentlichen Abschied (`pagehide`) entfernt. Steht sie beim
 *    Start noch da, endete die Seite ohne Abschied.
 *
 * ⚠️ Ein echter Kaltstart (App weggewischt) leert den sessionStorage — die
 *    Marke kann dann nicht fälschlich melden.
 */

const MARKE = "vespera:seite-laeuft";
/** Das Protokoll nimmt 400 Zeichen je Zeile; Platz für den Vorspann bleibt. */
const STUECK = 340;

interface AbsturzUmgebung {
  speicher: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  melde: (zeile: string) => void;
  /** Die nativen Berichte; null, wenn es keine Hülle gibt. */
  holeBerichte: (() => Promise<string[]>) | null;
  beimAbschied: (f: () => void) => void;
  jetzt?: () => number;
}

export async function absturzMelden(u: AbsturzUmgebung): Promise<void> {
  const jetzt = u.jetzt ?? Date.now;
  try {
    const vorher = u.speicher?.getItem(MARKE);
    if (vorher) {
      const sek = Math.round((jetzt() - Number(vorher)) / 1000);
      u.melde(
        `[absturz] seite neu gestartet ohne Abschied` +
          (Number.isFinite(sek) ? ` (lief ${sek}s)` : ""),
      );
    }
    u.speicher?.setItem(MARKE, String(jetzt()));
    u.beimAbschied(() => {
      try {
        u.speicher?.removeItem(MARKE);
      } catch {
        /* ohne Speicher keine Marke */
      }
    });
  } catch {
    /* ohne Speicher keine Marke */
  }

  if (!u.holeBerichte) return;
  let berichte: string[] = [];
  try {
    berichte = await u.holeBerichte();
  } catch {
    return;
  }
  for (const b of berichte) {
    const teile = Math.max(1, Math.ceil(b.length / STUECK));
    for (let i = 0; i < teile; i++) {
      u.melde(`[absturz] ${i + 1}/${teile} ${b.slice(i * STUECK, (i + 1) * STUECK)}`);
    }
  }
}
